import ArchiveIcon from "@mui/icons-material/Archive";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import MinorCrashIcon from "@mui/icons-material/MinorCrash";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { ErrorBoundary } from "@sentry/react";
import Cookies from "js-cookie";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import dark_burger_menu from "../../../assets/images/Menu/dark-theme/white_burger_menu.svg";
import dark_cedar_logo from "../../../assets/images/Menu/dark-theme/white_mobile_cedar_logo.svg";
import light_burger_menu from "../../../assets/images/Menu/light-theme/white_burger_menu.svg";
import light_cedar_logo from "../../../assets/images/Menu/light-theme/white_mobile_cedar_logo.svg";
import { DARK_ACCENT, GRAY, MENU_ELEMENTS } from "../../../constants";
import { ThemeContext } from "../../../context";
import { ProfilePicture } from "../../../pages/UserProfile/common/ProfilePicture";
import { defaultUrl } from "../../../utils/authentication";
import { eventTracking, MixpanelEvents } from "../../../utils/userTracking";
import { DarkLightToggle } from "../../Forms/common";

interface Props {
  activePage: string | undefined;
  firstName: string;
  lastName: string;
  email: string;
  cxoUrl: string;
  onClickCloseMenuHandler: () => void;
  onBurgerMenuClick: () => void;
}

const MenuItems = withRouter((props: Props & RouteComponentProps): JSX.Element => {
  const { activePage, onClickCloseMenuHandler, firstName, lastName, email, cxoUrl, history, onBurgerMenuClick } = props;

  const { theme } = useContext(ThemeContext);

  const croppedEmail = email?.split("@")[0];
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // State for accordion functionality
  const [openParent, setOpenParent] = useState<string>("");
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, string>>({});

  // Active parent and submenu ID states
  const [activeParentId, setActiveParentId] = useState<string>("");
  const [activeSubMenuId, setActiveSubMenuId] = useState<string>("");

  const screenWidth = window.matchMedia("(max-width: 768px)");
  useEffect(() => setIsMobile(screenWidth.matches), [screenWidth]);

  // Handles page navigation
  const onMenuItemClick = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      evt.stopPropagation();
      const {
        currentTarget: { dataset },
      } = evt;
      const pageLink = dataset?.page;

      if (pageLink === "/dashboard_info" || pageLink === "/user_profile") {
        eventTracking(MixpanelEvents.menu_click, { link: pageLink });
      }

      if (history.location.pathname === pageLink) {
        window.location.reload();
      }
      history.push({ pathname: pageLink, search: "" });

      // Close all menus after navigation
      setOpenParent("");
      setOpenSubMenus({});
      setActiveSubMenuId("");
    },
    [history]
  );

  const myMenuItems = useMemo(() => {
    const viewsCookie = Cookies.get("views") as string;

    const views: string[] = viewsCookie ? JSON.parse(viewsCookie) : [];

    if (views.includes("annotation_admin")) {
      views.push("admin");
    }

    if (!views?.length) return [];

    const parentMenuIds = Object.keys(MENU_ELEMENTS).reduce((result: string[], parent) => {
      const { has_children, id } = MENU_ELEMENTS[parent];
      if (!has_children) {
        if (views.includes(id)) return [...result, id];

        return result;
      }

      return MENU_ELEMENTS[parent].children.some((child) => views.some((view) => child.id.includes(view)))
        ? [...result, MENU_ELEMENTS[parent].id]
        : result;
    }, []);

    // return [...parentMenuIds, "adhoc_button", "abt_data_query", "correspondence_mapping", "pricing_project", "verbatim_analysis", ...views];
    return [...parentMenuIds, "adhoc_button", ...views];
  }, []);

  // Toggle parent menu
  const onParentClickHandler = (evt: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    const {
      currentTarget: { id: currentId },
    } = evt;

    if (openParent === currentId) {
      // Close if already open
      setOpenParent("");
      setOpenSubMenus({});
      setActiveParentId("");
      setActiveSubMenuId("");
    } else {
      // Open this parent and close others
      setOpenParent(currentId);
      setOpenSubMenus({});
      setActiveParentId(currentId);
      setActiveSubMenuId("");
    }
    setIsExpanded(true);
  };

  // Handler for submenu items with points of interest
  const onSubMenuClickHandler = (
    evt: React.MouseEvent<HTMLElement>,
    childId: string,
    hasPointsOfInterest: boolean,
    path: string,
    parentId: string
  ) => {
    if (!hasPointsOfInterest) {
      // If no points of interest, navigate directly
      setActiveParentId(parentId);
      setActiveSubMenuId(childId);
      onMenuItemClick(evt);
      return;
    }

    evt.stopPropagation();

    if (openSubMenus[parentId] === childId) {
      // Close if already open
      setOpenSubMenus((prev) => ({
        ...prev,
        [parentId]: "",
      }));
      setActiveSubMenuId("");
    } else {
      // Open this submenu
      setOpenSubMenus((prev) => ({
        ...prev,
        [parentId]: childId,
      }));
      setActiveSubMenuId(childId);
    }
  };

  const homeRedirect = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      evt.stopPropagation();

      const pageLink = "/home";

      history.push({ pathname: pageLink, search: "" });
      window.location.reload();
    },
    [history]
  );

  return (
    <ErrorBoundary>
      <div
        className={`cedar_logo home_navigation hide_on_mobile ${isExpanded ? "scrollexpanded" : ""}`}
        id="cedar_logo_desktop"
        data-page={defaultUrl}
        onClick={homeRedirect}
      >
        <div id="tree" />
        <div id="text" />
      </div>
      <div id="cedar_logo" data-page={defaultUrl} onClick={homeRedirect} className="home_navigation hide_on_desktop cedar_home_logo">
        <img className="hide_on_desktop" src={theme === "light" ? light_cedar_logo : dark_cedar_logo} alt="Cedar Logo" />
      </div>
      <div className="burger_div hide_on_desktop">
        <img id="burger_icon" src={theme === "light" ? light_burger_menu : dark_burger_menu} alt="menu" onClick={onBurgerMenuClick} />
      </div>
      <ul
        id="profile"
        className={`hide_on_mobile ${isExpanded ? "expanded" : ""}`}
        onMouseLeave={() => setIsExpanded(false)}
        data-test-id="menu"
      >
        <button id="closeMenu" onClick={onClickCloseMenuHandler}>
          &#x2573;
        </button>
        {Object.keys(MENU_ELEMENTS)
          .filter((menu_parent) => menu_parent !== "theme_mode" && menu_parent !== "profile")
          .map((menu_parent: string) => {
            if (!myMenuItems.includes(MENU_ELEMENTS[menu_parent]?.id)) return;
            const { id, name, description, hide_on_mobile, has_children, hidden, path, children } = MENU_ELEMENTS[menu_parent];
            const hiddenClass = hidden ? "hidden" : "";
            const hideOnMobileClass = hide_on_mobile ? "hide_on_mobile" : "";

            if (!has_children && path) {
              const pageUrlRe = new RegExp(path as string, "gi");
              const isActiveClass = activePage && pageUrlRe.test(activePage) ? "active" : "";

              return (
                <li
                  id={id}
                  key={id}
                  className={`profile_button ${hiddenClass} ${hideOnMobileClass} ${isActiveClass} ${
                    activeParentId === id ? "activeParent" : ""
                  }`}
                  data-page={path}
                  data-test-id={id}
                  onClick={(evt) => {
                    setActiveParentId(id);
                    onMenuItemClick(evt);
                  }}
                >
                  <div className="icon" />
                  <div className="text">
                    <p>{name}</p>
                  </div>
                </li>
              );
            } else {
              const hasActiveChildClass =
                children.filter((childElem) => {
                  const { path } = childElem;
                  const pageUrlRe = new RegExp(path, "gi");

                  return activePage && pageUrlRe.test(activePage);
                })?.length > 0
                  ? "active"
                  : "";

              const isParentOpen = openParent === id;

              const resultingElements = [
                <li
                  key={id}
                  id={id}
                  className={`profile_button ${hiddenClass} ${hideOnMobileClass} ${hasActiveChildClass} ${isParentOpen ? "isShown" : ""} ${
                    activeParentId === id ? "activeParent" : ""
                  }`}
                  data-test-id={id}
                  data-child-class={`${menu_parent}_child`}
                  onClick={onParentClickHandler}
                >
                  {id == "try_button" ? (
                    <SupportAgentIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  ) : id == "buy_button" ? (
                    <ShoppingCartIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  ) : id == "use_button" ? (
                    <MinorCrashIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  ) : id == "executive_reporting_button" ? (
                    <TrendingUpIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  ) : id == "adhoc_button" ? (
                    <DashboardCustomizeIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  ) : (
                    <ArchiveIcon className="icon" sx={{ color: theme === "light" ? DARK_ACCENT : GRAY, fontSize: "5px" }} />
                  )}

                  <div className="text">
                    <p>{name}</p>
                    <span className="menu_text_description">{description}</span>
                  </div>
                  <span className="menu_arrow_down" />
                </li>,
              ];

              children.map((child) => {
                const {
                  id: child_id,
                  name: child_name,
                  path,
                  hide_on_mobile: child_hide_on_mobile,
                  hidden: child_hidden,
                  points_of_interest: points_of_interest,
                } = child;

                const childPageUrl = new RegExp(path, "gi");

                if (child_hidden) return;

                const childHiddenClass = child_hidden ? "hidden" : "";
                const isActiveClass = activePage && childPageUrl.test(activePage) ? "active" : "";
                const childHideOnMobileClass = child_hide_on_mobile ? "hide_on_mobile" : "";
                const childClass = `${menu_parent}_child`;
                const hasPointsOfInterest = !!(points_of_interest && points_of_interest.length > 0);
                const isSubMenuOpen = openSubMenus[id] === child_id;
                const myStyles = {
                  display: "flex",
                  flexWrap: "wrap",
                  alignContent: "center",
                  flexFlow: "row",
                  alignItems: "center",
                  textDecoration: "none",
                };

                resultingElements.push(
                  child_name === "CXO EXPERIMENT LIBRARY" ? (
                    <a
                      key={child_id}
                      id={child_id}
                      data-test-id={"cxo_experiment_button"}
                      className={`profile_button child_button ${childClass} ${childHiddenClass} ${childHideOnMobileClass} ${isActiveClass} ${
                        isParentOpen ? "isShown" : ""
                      }`}
                      target="_blank"
                      href={cxoUrl}
                      //@ts-ignore
                      style={myStyles}
                      rel="noreferrer"
                      onClick={() => {
                        eventTracking(MixpanelEvents.cxo_access, { through: "Menu" });
                        setActiveParentId(id);
                      }}
                    >
                      <div className="icon" data-test-id="menu__info--icon" />
                      <div className="text" data-test-id="menu__info--text">
                        <p>CXO EXPERIMENT LIBRARY</p>
                      </div>
                    </a>
                  ) : (
                    <li
                      key={child_id}
                      id={child_id}
                      data-page={path}
                      className={`profile_button child_button ${childClass} ${childHiddenClass} ${childHideOnMobileClass} ${isActiveClass} ${
                        isParentOpen ? "isShown" : ""
                      } ${isSubMenuOpen ? "isShown" : ""} ${activeSubMenuId === child_id ? "activeSubMenu" : ""}`}
                      data-test-id={child_id}
                      onClick={(evt) => {
                        setActiveParentId(id);
                        onSubMenuClickHandler(evt, child_id, hasPointsOfInterest, path, id);
                      }}
                    >
                      <div className="icon" />
                      <div className="text">
                        <p>{child_name}</p>
                      </div>
                      {hasPointsOfInterest && <span className="menu_arrow_down" />}
                    </li>
                  )
                );

                // Add points of interest as nested child elements
                if (hasPointsOfInterest && points_of_interest) {
                  points_of_interest.forEach((poi, index) => {
                    const poiClass = `${child_id}_poi`;
                    const poiId = typeof poi === "string" ? `${child_id}_poi_${index}` : `${child_id}_poi_${poi.name}`;
                    const poiPath = typeof poi === "string" ? undefined : poi.path;

                    resultingElements.push(
                      <li
                        key={poiId}
                        id={poiId}
                        data-page={poiPath}
                        className={`profile_button child_button poi_button ${poiClass} ${childHiddenClass} ${childHideOnMobileClass} ${
                          isParentOpen && isSubMenuOpen ? "isShown" : ""
                        }`}
                        data-test-id={poiId}
                        onClick={(evt) => {
                          if (poiPath) {
                            setActiveParentId(id);
                            onMenuItemClick(evt);
                          }
                        }}
                        style={{ cursor: poiPath ? "pointer" : "default" }}
                      >
                        <div className="icon" />
                        <div className="text">
                          <p>{typeof poi === "string" ? poi : poi.name}</p>
                        </div>
                      </li>
                    );
                  });
                }
              });

              return resultingElements.map((element) => element);
            }
          })}

        {/* Render theme_mode and profile at the bottom */}
        {["theme_mode", "profile"]
          .filter((menu_parent) => MENU_ELEMENTS[menu_parent] && myMenuItems.includes(MENU_ELEMENTS[menu_parent]?.id))
          .map((menu_parent: string) => {
            const { id, name, description, hide_on_mobile, has_children, hidden, path, children } = MENU_ELEMENTS[menu_parent];
            const hiddenClass = hidden ? "hidden" : "";
            const hideOnMobileClass = hide_on_mobile ? "hide_on_mobile" : "";

            if (!has_children && path) {
              const pageUrlRe = new RegExp(path, "gi");
              const isActiveClass = activePage && pageUrlRe.test(activePage) ? "active" : "";

              return (
                <li
                  id={id}
                  key={id}
                  className={`profile_button ${hiddenClass} ${hideOnMobileClass} ${isActiveClass} ${
                    activeParentId === id ? "activeParent" : ""
                  }`}
                  data-page={path}
                  data-test-id={id}
                  onClick={(evt) => {
                    setActiveParentId(id);
                    onMenuItemClick(evt);
                  }}
                >
                  <div className="icon" />
                  <div className="text">
                    <p>{name}</p>
                  </div>
                </li>
              );
            } else {
              const hasActiveChildClass =
                children.filter((childElem) => {
                  const { path } = childElem;
                  const pageUrlRe = new RegExp(path, "gi");

                  return activePage && pageUrlRe.test(activePage);
                })?.length > 0
                  ? "active"
                  : "";

              const isParentOpen = openParent === id;

              const resultingElements =
                menu_parent === "profile"
                  ? [
                      <li
                        key={id}
                        id={id}
                        className={`profile_button ${hiddenClass} ${hideOnMobileClass} ${hasActiveChildClass} ${
                          isParentOpen ? "isShown" : ""
                        } ${activeParentId === id ? "activeParent" : ""}`}
                        data-test-id={id}
                        data-child-class={`${menu_parent}_child`}
                        onMouseEnter={!isMobile ? onParentClickHandler : undefined}
                        onTouchStart={onParentClickHandler}
                      >
                        <div className="icon">
                          <ProfilePicture firstname={firstName || email || " "} lastname={lastName || " "} />
                        </div>
                        <div className="text">
                          <p>{firstName && lastName ? `${firstName} ${lastName}` : email ? croppedEmail : "user profile"}</p>
                        </div>
                        <span className="menu_arrow_down hide_on_desktop" />
                      </li>,
                    ]
                  : menu_parent === "theme_mode"
                  ? [
                      <li key={id} id={id} className={`profile_button  ${hiddenClass} ${hideOnMobileClass}`} data-test-id={id}>
                        <div className="icon" />
                        <div className="toggle" style={{ fontSize: "var(--regular_font_size)", margin: "auto 0" }}>
                          <DarkLightToggle />
                        </div>
                      </li>,
                    ]
                  : [];

              children.map((child) => {
                const { id: child_id, name: child_name, path, hide_on_mobile: child_hide_on_mobile, hidden: child_hidden } = child;

                const childPageUrl = new RegExp(path, "gi");

                if (!myMenuItems.some((item) => child_id.includes(item))) return;

                const childHiddenClass = child_hidden ? "hidden" : "";
                const isActiveClass = activePage && childPageUrl.test(activePage) ? "active" : "";
                const childHideOnMobileClass = child_hide_on_mobile ? "hide_on_mobile" : "";
                const childClass = `${menu_parent}_child`;
                const myStyles = {
                  display: "flex",
                  flexWrap: "wrap",
                  alignContent: "center",
                  flexFlow: "row",
                  alignItems: "center",
                  textDecoration: "none",
                };

                resultingElements.push(
                  child_name === "CXO EXPERIMENT LIBRARY" ? (
                    <a
                      key={child_id}
                      id={child_id}
                      data-test-id={"cxo_experiment_button"}
                      className={`profile_button child_button ${childClass} ${childHiddenClass} ${childHideOnMobileClass} ${isActiveClass} ${
                        isParentOpen ? "isShown" : ""
                      }`}
                      target="_blank"
                      href={cxoUrl}
                      //@ts-ignore
                      style={myStyles}
                      rel="noreferrer"
                      onClick={() => {
                        eventTracking(MixpanelEvents.cxo_access, { through: "Menu" });
                        setActiveParentId(id);
                      }}
                    >
                      <div className="icon" data-test-id="menu__info--icon" />
                      <div className="text" data-test-id="menu__info--text">
                        <p>CXO EXPERIMENT LIBRARY</p>
                      </div>
                    </a>
                  ) : (
                    <li
                      key={child_id}
                      id={child_id}
                      data-page={path}
                      className={`profile_button child_button ${childClass} ${childHiddenClass} ${childHideOnMobileClass} ${isActiveClass} ${
                        isParentOpen ? "isShown" : ""
                      }`}
                      data-test-id={child_id}
                      onClick={(evt) => {
                        setActiveParentId(id);
                        onMenuItemClick(evt);
                      }}
                    >
                      <div className="icon" />
                      <div className="text">
                        <p>{child_name}</p>
                      </div>
                    </li>
                  )
                );
              });

              return resultingElements.map((element) => element);
            }
          })}
      </ul>
    </ErrorBoundary>
  );
});

export default MenuItems;
